import React, { useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Stack,
    TextField,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    Chip,
    Divider,
    Typography,
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import authService from '../api-authorization/AuthorizeService';

const BRIGHT_PALETTE = [
    '#FFD400', '#00E676', '#2979FF', '#FF5252',
    '#FF6D00', '#00B8D4', '#7C4DFF', '#C6FF00',
];

const pickFromPalette = () =>
    BRIGHT_PALETTE[Math.floor(Math.random() * BRIGHT_PALETTE.length)];

const AddLabelComponent = ({ brand_id, setLoading, labelAdded }) => {
    const [labelName, setLabelName] = useState('');
    const [colorValue, setColorValue] = useState(pickFromPalette());
    const [submitting, setSubmitting] = useState(false);
    const [touched, setTouched] = useState(false);

    const nameError = useMemo(
        () => touched && labelName.trim().length === 0,
        [touched, labelName]
    );

    function hexToRgbString(hex) {
        let c = hex.replace('#', '');
        if (c.length === 3) {
            // expand shorthand (#abc → #aabbcc)
            c = c.split('').map((ch) => ch + ch).join('');
        }
        const bigint = parseInt(c, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `${r},${g},${b}`;
    }
    
    const handleSubmit = async () => {
        setTouched(true);
        if (labelName.trim().length === 0) return;

        setSubmitting(true);
        setLoading(true);

        try {
            const req = {
                color_hex: hexToRgbString(colorValue).replace('#', ''), // add '#' if API expects it
                name: labelName.trim(),
                brand_id,
            };

            const response = await fetch('/api/organisation/labels', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(req),
            });

            authService.followIfLoginRedirect(response);
            if (response.ok) {
                labelAdded();
                setLabelName('');
                setColorValue(pickFromPalette());
                setTouched(false);
            }
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    return (
     
            <Grid container spacing={0} alignItems="flex-start">
                <Grid item xs={12}>
                    <Stack spacing={0}>
                        <TextField
                            autoFocus
                            label="Label name"
                            value={labelName}
                            onChange={(e) => setLabelName(e.target.value)}
                            onBlur={() => setTouched(true)}
                            error={nameError}
                            helperText={nameError ? 'Please enter a label name.' : ' '}
                            fullWidth
                            size="small"
                        />

                        <FormControl>
                            <FormLabel htmlFor="color-input">Color</FormLabel>
                            <MuiColorInput
                                id="color-input"
                                value={colorValue}
                                onChange={setColorValue}
                                format="hex"
                                isAlphaHidden
                                sx={{ mt: 1 }}
                                size="small"
                            />
                            <FormHelperText>Select a color or choose a quick pick.</FormHelperText>
                        </FormControl>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {BRIGHT_PALETTE.map((hex) => (
                                <Chip
                                    key={hex}
                                    label={hex.toUpperCase()}
                                    onClick={() => setColorValue(hex)}
                                    size="small"
                                    variant={hex === colorValue ? 'filled' : 'outlined'}
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: hex,
                                        bgcolor: hex === colorValue ? hex : 'transparent',
                                        color: hex === colorValue ? 'black' : 'inherit',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ pt: 1 }}>
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setLabelName('');
                                setColorValue(pickFromPalette());
                                setTouched(false);
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSubmit}
                            disabled={submitting}
                        >
                            {submitting ? 'Adding…' : 'Add Label'}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
    
    );
};

export default AddLabelComponent;
